import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  LinearProgress,
  Button,
  Tabs,
  Tab,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import color from "../../components/utils/Colors";
import { useSnackbar } from "../../components/utils/SnackBar";
import { getEnrolledCourses, getDataFromTable } from "../../services/services";
import { useUser } from "../../services/Context/UserContext";

interface Course {
  id: string;
  title: string;
  instructor: string;
  progress: number;
  image: string;
  duration: string;
  completedLessons: number;
  totalLessons: number;
  lastAccessed: string;
  status: "in-progress" | "completed" | "not-started";
}

const MyLearning: React.FC = () => {
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();
  const isMobile = useMediaQuery("(max-width:600px)");
  const { user } = useUser();
  const [currentTab, setCurrentTab] = useState(0);
  const [enrolledCourses, setEnrolledCourses] = useState<Course[]>([]);
  const [filteredCourses, setFilteredCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(false);

  const mapApiCourse = (item: any): Course => {
    const course = item?.course ?? item;
    const progress = item?.progress ?? 0;
    let status: Course["status"] = "not-started";
    if (progress >= 100) status = "completed";
    else if (progress > 0) status = "in-progress";

    return {
      id: String(item?.courseId ?? course?.id ?? ""),
      title: course?.title ?? course?.courseName ?? "Course",
      instructor:
        course?.teachersName ??
        (Array.isArray(course?.teachers) ? course.teachers.join(", ") : ""),
      progress,
      image: course?.image ?? course?.coverImage,
      duration: course?.timeline ?? course?.duration ?? "",
      completedLessons: item?.completedLessons ?? 0,
      totalLessons: item?.totalLessons ?? 0,
      lastAccessed: item?.updatedAt
        ? new Date(item.updatedAt).toLocaleDateString()
        : "N/A",
      status,
    };
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!user?.id) return;
    setLoading(true);
    getEnrolledCourses(user.id)
      .then(async (res: any) => {
        const data: any[] = res?.data?.data ?? [];
        const mapped = await Promise.all(
          data.map(async (item: any) => {
            let courseRes;
            if (item.courseId) {
              courseRes = await getDataFromTable("course", {
                id: item.courseId,
              });
            }
            const courseData = courseRes?.data?.data?.[0] || item?.course || {};
            return mapApiCourse({ ...item, course: courseData });
          }),
        );
        setEnrolledCourses(mapped);
        setFilteredCourses(mapped);
      })
      .catch(() => {
        showSnackbar("Failed to load courses");
      })
      .finally(() => setLoading(false));
  }, [user?.id]);

  const filterCourses = (tabIndex: number) => {
    setCurrentTab(tabIndex);
    let filtered = enrolledCourses;
    switch (tabIndex) {
      case 1:
        filtered = enrolledCourses.filter((c) => c.status === "in-progress");
        break;
      case 2:
        filtered = enrolledCourses.filter((c) => c.status === "completed");
        break;
      case 3:
        filtered = enrolledCourses.filter((c) => c.status === "not-started");
        break;
      default:
        filtered = enrolledCourses;
    }
    setFilteredCourses(filtered);
  };

  useEffect(() => {
    filterCourses(currentTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrolledCourses]);

  const handleContinueLearning = (courseId: string, courseTitle: string) => {
    navigate(`/open-course/${courseId}`);
    showSnackbar(`Continuing "${courseTitle}"...`);
  };

  const handleBookmark = (courseTitle: string) => {
    showSnackbar(`Added "${courseTitle}" to wishlist`);
  };

  const CourseCard = ({ course }: { course: Course }) => (
    <Card
      sx={{
        boxShadow: "10px 20px 20px rgba(0, 0, 0, 0.04)",
        borderRadius: "12px",
        overflow: "hidden",
        transition: "transform 0.3s ease, box-shadow 0.3s ease",
        "&:hover": {
          transform: "translateY(-5px)",
          boxShadow: "10px 20px 30px rgba(0, 0, 0, 0.1)",
        },
      }}
    >
      <Box sx={{ position: "relative" }}>
        <CardMedia
          component="img"
          height={isMobile ? "150" : "200"}
          image={process.env.REACT_APP_PUBLIC_BASE_URL+course.image}
          alt={course.title}
        />
        <Box
          sx={{
            position: "absolute",
            top: "10px",
            right: "10px",
            background: "white",
            borderRadius: "50%",
            padding: "8px",
            cursor: "pointer",
            transition: "transform 0.2s ease",
            "&:hover": { transform: "scale(1.1)" },
          }}
          onClick={() => handleBookmark(course.title)}
        >
          <BookmarkIcon sx={{ color: color.firstColor, fontSize: "20px" }} />
        </Box>

        {course.status === "completed" && (
          <Box
            sx={{
              position: "absolute",
              top: "10px",
              left: "10px",
              background: "rgba(0, 0, 0, 0.6)",
              color: "white",
              padding: "4px 12px",
              borderRadius: "20px",
              fontSize: "12px",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              gap: "5px",
            }}
          >
            <CheckCircleIcon sx={{ fontSize: "16px" }} /> Completed
          </Box>
        )}
      </Box>

      <CardContent sx={{ padding: "16px" }}>
        <Typography
          variant="h6"
          sx={{
            color: color.textColor,
            fontWeight: "bold",
            marginBottom: "8px",
            minHeight: "40px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {course.title}
        </Typography>

        {course.instructor && (
          <Typography
            variant="body2"
            sx={{ color: "#666", marginBottom: "12px" }}
          >
            By {course.instructor}
          </Typography>
        )}

        <Box sx={{ marginBottom: "12px" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "6px",
            }}
          >
            <Typography variant="caption" sx={{ color: "#666" }}>
              Progress
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: color.firstColor, fontWeight: "bold" }}
            >
              {course.progress}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={course.progress}
            sx={{
              height: "8px",
              borderRadius: "4px",
              backgroundColor: "#e0e0e0",
              "& .MuiLinearProgress-bar": { backgroundColor: color.firstColor },
            }}
          />
        </Box>

        {(course.completedLessons > 0 || course.totalLessons > 0) && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "12px",
            }}
          >
            <Typography variant="caption" sx={{ color: "#666" }}>
              {course.completedLessons} of {course.totalLessons} lessons
            </Typography>
            <Typography variant="caption" sx={{ color: "#999" }}>
              {course.duration}
            </Typography>
          </Box>
        )}

        <Typography
          variant="caption"
          sx={{ color: "#999", display: "block", marginBottom: "12px" }}
        >
          Last accessed: {course.lastAccessed}
        </Typography>

        <Button
          fullWidth
          variant="contained"
          startIcon={<PlayArrowIcon />}
          sx={{
            background: color.firstColor,
            color: "white",
            textTransform: "none",
            fontWeight: "bold",
            borderRadius: "8px",
            "&:hover": { background: color.forthColor },
          }}
          onClick={() => handleContinueLearning(course.id, course.title)}
        >
          {course.progress === 0 ? "Start Learning" : "Continue Learning"}
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <Box
      sx={{ minHeight: "100vh", background: "transparent", paddingY: "40px" }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ marginBottom: "40px" }}>
          <Typography
            variant="h4"
            sx={{
              color: color.textColor,
              fontWeight: "bold",
              marginBottom: "8px",
            }}
          >
            My Learning
          </Typography>
          <Typography variant="body1" sx={{ color: "#666" }}>
            Continue your learning journey with {enrolledCourses.length}{" "}
            enrolled course(s)
          </Typography>
        </Box>

        {/* Stats */}
        <Grid container spacing={2} sx={{ marginBottom: "40px" }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)" }}>
              <CardContent sx={{ textAlign: "center", padding: "20px" }}>
                <Typography
                  variant="h4"
                  sx={{ color: color.firstColor, fontWeight: "bold" }}
                >
                  {enrolledCourses.length}
                </Typography>
                <Typography variant="body2" sx={{ color: "#666" }}>
                  Enrolled Courses
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)" }}>
              <CardContent sx={{ textAlign: "center", padding: "20px" }}>
                <Typography
                  variant="h4"
                  sx={{ color: color.thirdColor, fontWeight: "bold" }}
                >
                  {
                    enrolledCourses.filter((c) => c.status === "in-progress")
                      .length
                  }
                </Typography>
                <Typography variant="body2" sx={{ color: "#666" }}>
                  In Progress
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)" }}>
              <CardContent sx={{ textAlign: "center", padding: "20px" }}>
                <Typography
                  variant="h4"
                  sx={{ color: "#4caf50", fontWeight: "bold" }}
                >
                  {
                    enrolledCourses.filter((c) => c.status === "completed")
                      .length
                  }
                </Typography>
                <Typography variant="body2" sx={{ color: "#666" }}>
                  Completed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)" }}>
              <CardContent sx={{ textAlign: "center", padding: "20px" }}>
                <Typography
                  variant="h4"
                  sx={{ color: "#ff9800", fontWeight: "bold" }}
                >
                  {enrolledCourses.length > 0
                    ? Math.round(
                        enrolledCourses.reduce(
                          (sum, c) => sum + c.progress,
                          0,
                        ) / enrolledCourses.length,
                      )
                    : 0}
                  %
                </Typography>
                <Typography variant="body2" sx={{ color: "#666" }}>
                  Avg Progress
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Box
          sx={{
            background: "white",
            borderRadius: "8px",
            boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)",
            marginBottom: "30px",
          }}
        >
          <Tabs
            value={currentTab}
            onChange={(e, newValue) => filterCourses(newValue)}
            sx={{
              borderBottom: `2px solid ${color.secondColor}`,
              "& .MuiTab-root": {
                textTransform: "none",
                fontSize: "16px",
                fontWeight: "500",
                color: "#999",
                "&.Mui-selected": {
                  color: color.firstColor,
                  fontWeight: "bold",
                },
              },
            }}
          >
            <Tab label={`All (${enrolledCourses.length})`} />
            <Tab
              label={`In Progress (${enrolledCourses.filter((c) => c.status === "in-progress").length})`}
            />
            <Tab
              label={`Completed (${enrolledCourses.filter((c) => c.status === "completed").length})`}
            />
            <Tab
              label={`Not Started (${enrolledCourses.filter((c) => c.status === "not-started").length})`}
            />
          </Tabs>

          {/* Courses Grid */}
          <Box sx={{ padding: "30px" }}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress sx={{ color: color.firstColor }} />
              </Box>
            ) : filteredCourses.length > 0 ? (
              <Grid container spacing={3}>
                {filteredCourses.map((course) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={course.id}>
                    <CourseCard course={course} />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{ textAlign: "center", padding: "60px 20px" }}>
                <Typography
                  variant="h6"
                  sx={{ color: "#999", marginBottom: "10px" }}
                >
                  No courses found
                </Typography>
                <Typography variant="body2" sx={{ color: "#bbb" }}>
                  {currentTab === 1 && "You haven't started any courses yet."}
                  {currentTab === 2 && "You haven't completed any courses yet."}
                  {currentTab === 3 && "No courses waiting to be started."}
                  {currentTab === 0 &&
                    "You haven't enrolled in any courses yet."}
                </Typography>
                <Button
                  variant="contained"
                  sx={{
                    marginTop: "20px",
                    background: color.firstColor,
                    textTransform: "none",
                  }}
                  onClick={() => navigate("/course")}
                >
                  Explore Courses
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default MyLearning;
